class HabitService {
  constructor() {
    this.storageKey = 'habitData';
    this.streakKey = 'studyStreak';
    this.achievementsKey = 'achievements';
    this.analyticsKey = 'studyAnalytics';
  }

  /**
   * Get current habit data
   */
  getHabitData() {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : this.getDefaultHabitData();
    } catch (error) {
      console.error('Error loading habit data:', error);
      return this.getDefaultHabitData();
    }
  }

  /**
   * Get default habit data structure
   */
  getDefaultHabitData() {
    return {
      currentStreak: 0,
      longestStreak: 0,
      totalStudyDays: 0,
      lastStudyDate: null,
      studyHistory: [],
      weeklyGoals: {
        studyDays: 5,
        studyHours: 20,
        subjects: 3
      },
      achievements: [],
      notifications: {
        enabled: true,
        reminderTime: '18:00',
        days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
      }
    };
  }

  /**
   * Record a study session
   */
  recordStudySession(sessionData) {
    const habitData = this.getHabitData();
    const today = new Date().toDateString();
    const now = new Date();

    // Check if already recorded today
    const todaySession = habitData.studyHistory.find(session => 
      new Date(session.date).toDateString() === today
    );

    if (todaySession) {
      // Update existing session
      todaySession.duration += sessionData.duration || 0;
      todaySession.subjects = [...new Set([...todaySession.subjects, ...(sessionData.subjects || [])])];
      todaySession.activities = [...new Set([...todaySession.activities, ...(sessionData.activities || [])])];
    } else {
      // Add new session
      habitData.studyHistory.push({
        date: now.toISOString(),
        duration: sessionData.duration || 0,
        subjects: sessionData.subjects || [],
        activities: sessionData.activities || [],
        completed: sessionData.completed || false
      });
    }

    // Update streak
    this.updateStreak(habitData, today);
    
    // Check achievements
    this.checkAchievements(habitData);
    
    // Save data
    this.saveHabitData(habitData);
    
    return habitData;
  }

  /**
   * Update study streak
   */
  updateStreak(habitData, today) {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toDateString();
    
    const lastStudyDate = habitData.lastStudyDate ? 
      new Date(habitData.lastStudyDate).toDateString() : null;

    if (lastStudyDate === yesterdayStr || lastStudyDate === today) {
      // Consecutive day
      habitData.currentStreak++;
    } else if (lastStudyDate !== today) {
      // Break in streak
      habitData.currentStreak = 1;
    }

    // Update longest streak
    if (habitData.currentStreak > habitData.longestStreak) {
      habitData.longestStreak = habitData.currentStreak;
    }

    habitData.lastStudyDate = new Date().toISOString();
    habitData.totalStudyDays++;
  }

  /**
   * Check and award achievements
   */
  checkAchievements(habitData) {
    const achievements = this.getAchievements();
    const newAchievements = [];

    achievements.forEach(achievement => {
      if (!habitData.achievements.includes(achievement.id)) {
        if (this.hasEarnedAchievement(achievement, habitData)) {
          newAchievements.push(achievement);
          habitData.achievements.push(achievement.id);
        }
      }
    });

    return newAchievements;
  }

  /**
   * Check if user has earned an achievement
   */
  hasEarnedAchievement(achievement, habitData) {
    switch (achievement.type) {
      case 'streak':
        return habitData.currentStreak >= achievement.requirement;
      case 'total_days':
        return habitData.totalStudyDays >= achievement.requirement;
      case 'longest_streak':
        return habitData.longestStreak >= achievement.requirement;
      case 'weekly_goal':
        return this.hasMetWeeklyGoal(habitData, achievement.requirement);
      default:
        return false;
    }
  }

  /**
   * Check if weekly goal is met
   */
  hasMetWeeklyGoal(habitData, goalType) {
    const weekStart = new Date();
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    weekStart.setHours(0, 0, 0, 0);

    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);

    const weekSessions = habitData.studyHistory.filter(session => {
      const sessionDate = new Date(session.date);
      return sessionDate >= weekStart && sessionDate <= weekEnd;
    });

    switch (goalType) {
      case 'study_days':
        return weekSessions.length >= habitData.weeklyGoals.studyDays;
      case 'study_hours':
        const totalHours = weekSessions.reduce((sum, session) => sum + session.duration, 0);
        return totalHours >= habitData.weeklyGoals.studyHours;
      case 'subjects':
        const uniqueSubjects = new Set();
        weekSessions.forEach(session => {
          session.subjects.forEach(subject => uniqueSubjects.add(subject));
        });
        return uniqueSubjects.size >= habitData.weeklyGoals.subjects;
      default:
        return false;
    }
  }

  /**
   * Get available achievements
   */
  getAchievements() {
    return [
      {
        id: 'first_day',
        title: 'First Steps',
        description: 'Complete your first study session',
        type: 'total_days',
        requirement: 1,
        icon: '🌟',
        coins: 10
      },
      {
        id: 'week_streak',
        title: 'Week Warrior',
        description: 'Maintain a 7-day study streak',
        type: 'streak',
        requirement: 7,
        icon: '🔥',
        coins: 50
      },
      {
        id: 'month_streak',
        title: 'Monthly Master',
        description: 'Maintain a 30-day study streak',
        type: 'streak',
        requirement: 30,
        icon: '👑',
        coins: 200
      },
      {
        id: 'hundred_days',
        title: 'Century Club',
        description: 'Study for 100 total days',
        type: 'total_days',
        requirement: 100,
        icon: '💎',
        coins: 500
      },
      {
        id: 'weekly_goal_days',
        title: 'Goal Getter',
        description: 'Meet your weekly study days goal',
        type: 'weekly_goal',
        requirement: 'study_days',
        icon: '🎯',
        coins: 25
      },
      {
        id: 'weekly_goal_hours',
        title: 'Time Master',
        description: 'Meet your weekly study hours goal',
        type: 'weekly_goal',
        requirement: 'study_hours',
        icon: '⏰',
        coins: 30
      },
      {
        id: 'weekly_goal_subjects',
        title: 'Subject Explorer',
        description: 'Study multiple subjects in a week',
        type: 'weekly_goal',
        requirement: 'subjects',
        icon: '📚',
        coins: 40
      },
      {
        id: 'longest_streak_50',
        title: 'Streak Champion',
        description: 'Achieve a 50-day longest streak',
        type: 'longest_streak',
        requirement: 50,
        icon: '🏆',
        coins: 300
      }
    ];
  }

  /**
   * Get study analytics
   */
  getStudyAnalytics() {
    const habitData = this.getHabitData();
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    // Filter recent sessions
    const recentSessions = habitData.studyHistory.filter(session => 
      new Date(session.date) >= thirtyDaysAgo
    );

    // Calculate analytics
    const analytics = {
      totalSessions: recentSessions.length,
      totalHours: recentSessions.reduce((sum, session) => sum + session.duration, 0),
      averageSessionLength: recentSessions.length > 0 ? 
        recentSessions.reduce((sum, session) => sum + session.duration, 0) / recentSessions.length : 0,
      mostStudiedSubject: this.getMostStudiedSubject(recentSessions),
      studyDaysThisMonth: new Set(recentSessions.map(s => 
        new Date(s.date).toDateString()
      )).size,
      completionRate: recentSessions.length > 0 ? 
        (recentSessions.filter(s => s.completed).length / recentSessions.length) * 100 : 0,
      weeklyTrend: this.getWeeklyTrend(recentSessions),
      subjectBreakdown: this.getSubjectBreakdown(recentSessions)
    };

    return analytics;
  }

  /**
   * Get most studied subject
   */
  getMostStudiedSubject(sessions) {
    const subjectCount = {};
    sessions.forEach(session => {
      session.subjects.forEach(subject => {
        subjectCount[subject] = (subjectCount[subject] || 0) + 1;
      });
    });

    const sortedSubjects = Object.entries(subjectCount)
      .sort(([,a], [,b]) => b - a);
    
    return sortedSubjects.length > 0 ? sortedSubjects[0][0] : null;
  }

  /**
   * Get weekly trend
   */
  getWeeklyTrend(sessions) {
    const weeklyData = {};
    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    daysOfWeek.forEach(day => {
      weeklyData[day] = 0;
    });

    sessions.forEach(session => {
      const day = daysOfWeek[new Date(session.date).getDay()];
      weeklyData[day] += session.duration;
    });

    return weeklyData;
  }

  /**
   * Get subject breakdown
   */
  getSubjectBreakdown(sessions) {
    const subjectData = {};
    sessions.forEach(session => {
      session.subjects.forEach(subject => {
        if (!subjectData[subject]) {
          subjectData[subject] = { sessions: 0, hours: 0 };
        }
        subjectData[subject].sessions++;
        subjectData[subject].hours += session.duration;
      });
    });

    return subjectData;
  }

  /**
   * Set weekly goals
   */
  setWeeklyGoals(goals) {
    const habitData = this.getHabitData();
    habitData.weeklyGoals = { ...habitData.weeklyGoals, ...goals };
    this.saveHabitData(habitData);
  }

  /**
   * Set notification preferences
   */
  setNotificationPreferences(preferences) {
    const habitData = this.getHabitData();
    habitData.notifications = { ...habitData.notifications, ...preferences };
    this.saveHabitData(habitData);
  }

  /**
   * Get streak insights
   */
  getStreakInsights() {
    const habitData = this.getHabitData();
    const insights = [];

    if (habitData.currentStreak >= 7) {
      insights.push({
        type: 'success',
        message: `🔥 Amazing! You're on a ${habitData.currentStreak}-day streak!`,
        icon: '🔥'
      });
    } else if (habitData.currentStreak >= 3) {
      insights.push({
        type: 'info',
        message: `Great start! You're building a ${habitData.currentStreak}-day streak.`,
        icon: '🌟'
      });
    }

    if (habitData.longestStreak > habitData.currentStreak) {
      insights.push({
        type: 'motivation',
        message: `Your longest streak was ${habitData.longestStreak} days. You can beat it!`,
        icon: '🏆'
      });
    }

    return insights;
  }

  /**
   * Get study recommendations
   */
  getStudyRecommendations() {
    const analytics = this.getStudyAnalytics();
    const recommendations = [];

    if (analytics.averageSessionLength < 1) {
      recommendations.push({
        type: 'duration',
        message: 'Try studying for longer sessions to improve retention',
        icon: '⏰'
      });
    }

    if (analytics.completionRate < 80) {
      recommendations.push({
        type: 'completion',
        message: 'Focus on completing your planned study sessions',
        icon: '✅'
      });
    }

    if (Object.keys(analytics.subjectBreakdown).length < 2) {
      recommendations.push({
        type: 'variety',
        message: 'Try studying multiple subjects for better balance',
        icon: '📚'
      });
    }

    return recommendations;
  }

  /**
   * Save habit data
   */
  saveHabitData(habitData) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(habitData));
    } catch (error) {
      console.error('Error saving habit data:', error);
    }
  }

  /**
   * Reset habit data (for testing)
   */
  resetHabitData() {
    localStorage.removeItem(this.storageKey);
    localStorage.removeItem(this.streakKey);
    localStorage.removeItem(this.achievementsKey);
    localStorage.removeItem(this.analyticsKey);
  }
}

export default new HabitService(); 